import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import type { ProjectTask, TaskNote } from '../types';

export function useTaskDetail(taskId: string | undefined) {
  const { user } = useAuth();
  const [task, setTask] = useState<ProjectTask | null>(null);
  const [notes, setNotes] = useState<TaskNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTask = useCallback(async () => {
    if (!taskId) return;
    setLoading(true);
    setError(null);

    try {
      const [{ data: taskData, error: tErr }, { data: notesData, error: nErr }] =
        await Promise.all([
          supabase.from('intern_project_tasks_view').select('*').eq('id', taskId).single(),
          supabase
            .from('intern_task_notes')
            .select('*')
            .eq('task_id', taskId)
            .order('created_at', { ascending: true }),
        ]);

      if (tErr) throw tErr;
      if (nErr) throw nErr;

      setTask(taskData as ProjectTask);
      setNotes(notesData as TaskNote[] ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load task.');
    } finally {
      setLoading(false);
    }
  }, [taskId]);

  useEffect(() => {
    fetchTask();
  }, [fetchTask]);

  const updateStatus = useCallback(async (status: string) => {
    if (!taskId) return { error: new Error('No task') };
    const { error } = await supabase.rpc('set_intern_task_status', {
      p_task_id: taskId,
      p_status: status,
    });
    if (!error) {
      setTask((t) => t ? { ...t, status: status as ProjectTask['status'] } : t);
    }
    return { error };
  }, [taskId]);

  const addNote = useCallback(async (body: string, isBlocker = false) => {
    if (!taskId || !user?.id) return { error: new Error('Not ready') };

    // Insert the note
    const { data: note, error: nErr } = await supabase
      .from('intern_task_notes')
      .insert({ task_id: taskId, author: 'worker', body, is_blocker: isBlocker })
      .select()
      .single();
    if (nErr) return { error: nErr };

    // If blocker, update task status
    if (isBlocker) {
      await supabase.rpc('set_intern_task_status', { p_task_id: taskId, p_status: 'blocked' });
      setTask((t) => t ? { ...t, status: 'blocked' } : t);
    }

    setNotes((prev) => [...prev, note as TaskNote]);
    return { error: null };
  }, [taskId, user?.id]);

  const resolveBlocker = useCallback(async (noteId: string) => {
    if (!taskId) return { error: new Error('No task') };
    const { error } = await supabase.rpc('resolve_intern_blocker', {
      p_note_id: noteId,
      p_task_id: taskId,
    });
    if (!error) {
      setNotes((prev) =>
        prev.map((n) =>
          n.id === noteId ? { ...n, resolved: true, resolved_at: new Date().toISOString() } : n
        )
      );
      setTask((t) => t ? { ...t, status: 'in_progress' } : t);
    }
    return { error };
  }, [taskId]);

  return { task, notes, loading, error, updateStatus, addNote, resolveBlocker, refresh: fetchTask };
}
